import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

/**
 * TabPanel Component
 * A reusable component for rendering tab content in MUI Tabs
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to render when the tab is active
 * @param {number} props.value - The currently selected tab index
 * @param {number} props.index - This tab's index
 * @param {string} [props.id] - Optional ID prefix for accessibility
 * @param {number} [props.padding] - Padding value (default: 3)
 * @param {Object} [props.sx] - Additional MUI sx styles
 */
function TabPanel({ children, value, index, id, padding = 3, sx = {}, ...other }) {
  const panelId = id ? `${id}-tabpanel-${index}` : undefined;
  const labelledBy = id ? `${id}-tab-${index}` : undefined;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={panelId}
      aria-labelledby={labelledBy}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: padding, ...sx }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  id: PropTypes.string,
  padding: PropTypes.number,
  sx: PropTypes.object,
};

/**
 * Helper function to generate a11y props for tab buttons
 *
 * @param {number} index - The tab index
 * @param {string} [id] - Optional ID prefix
 * @returns {Object} Props to spread on the Tab component
 *
 * Usage:
 *   <Tab label="Item One" {...a11yProps(0)} />
 *   <Tab label="Item Two" {...a11yProps(1)} />
 */
export function a11yProps(index, id = 'tab') {
  return {
    id: `${id}-tab-${index}`,
    'aria-controls': `${id}-tabpanel-${index}`,
  };
}

export default TabPanel;
